<script setup lang="ts">
const colorMode = useColorMode();
const isLight = computed(() => colorMode.value !== "dark");

interface ScenarioCard {
  title: string;
  problem: string;
  solution: string;
  result: string;
}

const props = withDefaults(
  defineProps<{
    heading?: string;
    subcopy?: string;
    cards: ScenarioCard[];
    labels?: { problem: string; solution: string; result: string };
  }>(),
  {
    heading: "",
    subcopy: "",
    labels: () => ({
      problem: "Problem",
      solution: "Solution",
      result: "Result",
    }),
  },
);
</script>

<template>
  <section
    class="mt-24 lg:mt-[100px] mb-16 lg:mb-[100px]"
    role="region"
    :aria-label="heading || 'Scenario cards'"
  >
    <!-- Heading -->
    <div v-if="heading" class="text-center mb-10">
      <h2
        class="text-[28px] lg:text-[48px] font-semibold leading-tight font-avant-demi text-transparent bg-clip-text bg-gradient-to-r from-[#12DBFF] to-[#BA55FE]"
      >
        {{ heading }}
      </h2>
      <p
        v-if="subcopy"
        class="text-[16px] lg:text-[18px] font-normal tracking-[0.36px] mt-4 font-avant"
        :class="isLight ? 'text-black' : 'text-white'"
      >
        {{ subcopy }}
      </p>
    </div>

    <div
      class="grid grid-cols-1 lg:grid-cols-3 gap-6 px-4 max-w-[1100px] mx-auto"
    >
      <article
        v-for="(card, idx) in cards"
        :key="idx"
        class="rounded-[8px] p-6 flex flex-col border overflow-hidden h-auto"
        :class="
          isLight
            ? 'border-[#E7CFFF] bg-[linear-gradient(32deg,#FFF_-74.45%,#FFF_100.91%)]'
            : 'border-[#3D1B60] bg-[linear-gradient(32deg,#2B0152_-74.45%,#12002C_100.91%)]'
        "
      >
        <span
          class="font-avant-demi text-[40px] font-semibold text-transparent"
          :class="
            isLight
              ? '[-webkit-text-stroke:1.5px_rgba(97,63,170,0.5)]'
              : '[-webkit-text-stroke:1.5px_rgba(255,255,255,0.5)]'
          "
        >
          0{{ idx + 1 }}
        </span>

        <h3
          class="font-semibold font-avant text-[18px] leading-normal mt-2"
          :class="isLight ? 'text-[#613FAA]' : 'text-[#F0DAFF]'"
          v-html="card.title"
        ></h3>

        <div class="mt-4">
          <span
            class="text-[12px] font-semibold tracking-[0.5px] uppercase font-avant"
            :class="isLight ? 'text-[#613FAA]' : 'text-[#A561FF]'"
          >
            {{ labels.problem }}
          </span>
          <p
            class="mt-1 text-[14px] leading-[20px] font-avant"
            :class="isLight ? 'text-black' : 'text-white/70'"
          >
            {{ card.problem }}
          </p>
        </div>

        <div class="mt-4">
          <span
            class="text-[12px] font-semibold tracking-[0.5px] uppercase font-avant"
            :class="isLight ? 'text-[#613FAA]' : 'text-[#A561FF]'"
          >
            {{ labels.solution }}
          </span>
          <p
            class="mt-1 text-[14px] leading-[20px] font-avant"
            :class="isLight ? 'text-black' : 'text-white/70'"
          >
            {{ card.solution }}
          </p>
        </div>

        <div class="mt-auto pt-4">
          <span
            class="text-[12px] font-semibold tracking-[0.5px] uppercase font-avant"
            :class="isLight ? 'text-[#613FAA]' : 'text-[#A561FF]'"
          >
            {{ labels.result }}
          </span>
          <p
            class="mt-1 text-[15px] leading-[20px] font-avant font-semibold"
            :class="isLight ? 'text-[#501D71]' : 'text-[#AF66E4]'"
          >
            {{ card.result }}
          </p>
        </div>
      </article>
    </div>
  </section>
</template>
